import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaQuoteLeft, FaStar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './Testimonials.css';

interface Testimonial {
  id: number;
  text: string;
  author: string;
  company: string;
  role: string;
  rating: number;
  project: string;
  image: string;
  results: {
    metric: string;
    improvement: string;
  };
}

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);

  const testimonials: Testimonial[] = [
    {
      id: 1,
      text: "This project was about creating AI agents and a platform to integrate and manage them. Haider was the brains of this project, where multiple other engineers contributed as well. He is a great (software) engineer and a leader people are ready to follow. Among the used technologies and methodologies were RAG, Playwright, ReAct, Chain of Thoughts, and many other related ones. I can approve Haider's professional skills as well as all the soft skills that are required to succeed in a project, such as communication, reliability, leadership, etc. Therefore, I look forward to working with Haider in the soon future!",
      author: "Jeshurun",
      company: "Client Project, Germany",
      role: "Project Lead",
      rating: 5,
      project: "AI Agent Platform & Integration",
      image: "/api/placeholder/80/80",
      results: {
        metric: "100%",
        improvement: "Project delivery & leadership"
      }
    },
    {
      id: 2,
      text: "Haider was very responsive, receptive to what we were trying to achieve and clear in what needed to be done to achieve our technical goals. He kept a consistent line of communication, even outside working hours when we ran into issues in order to resolve them. We were able to conveniently navigate through the objectives and address the challenges that arose throughout.",
      author: "PJ Byrne",
      company: "Client Project, Bahrain",
      role: "Project Manager",
      rating: 5,
      project: "WhatsApp Chat Automation",
      image: "/api/placeholder/80/80",
      results: {
        metric: "100%",
        improvement: "Project satisfaction"
      }
    },
    {
      id: 3,
      text: "Haider did an excellent job on the backend and AI integrations. He understood the requirements quickly, implemented everything cleanly, and communicated clearly throughout the project. Very reliable and technically strong — I'd be happy to work with him again.",
      author: "Yaron Been",
      company: "Client Project, USA",
      role: "Client",
      rating: 5,
      project: "Backend & AI Integrations",
      image: "/api/placeholder/80/80",
      results: {
        metric: "100%",
        improvement: "Technical delivery"
      }
    },
    {
      id: 4,
      text: "Haider is the perfect professional. He will go above and beyond to complete the project to the highest standards.",
      author: "Andreas Arghyrou",
      company: "Client Project, UK",
      role: "Business Owner",
      rating: 5,
      project: "Market Research Reports",
      image: "/api/placeholder/80/80",
      results: {
        metric: "100%",
        improvement: "Quality standards met"
      }
    },
    {
      id: 5,
      text: "Haider did an amazing job helping us set up our AI agency custom agents.",
      author: "Sam",
      company: "Vander Group, USA",
      role: "Founder",
      rating: 5,
      project: "AI Agency Custom Agents",
      image: "/api/placeholder/80/80",
      results: {
        metric: "100%",
        improvement: "Custom agent deployment"
      }
    },
    {
      id: 6,
      text: "Fast and reliable. I recommend Haider for automation work.",
      author: "Lars",
      company: "Rocket Conversion, Hong Kong",
      role: "Client",
      rating: 5,
      project: "Automation Work",
      image: "/api/placeholder/80/80",
      results: {
        metric: "100%",
        improvement: "Fast & reliable delivery"
      }
    },
    {
      id: 7,
      text: "Great job, we are delighted. Will continue working with him in the near future.",
      author: "Sharon Sciammas",
      company: "Client Project, Netherlands",
      role: "Marketing Director",
      rating: 5,
      project: "Keyword Research Automation",
      image: "/api/placeholder/80/80",
      results: {
        metric: "100%",
        improvement: "Client satisfaction"
      }
    },
    {
      id: 8,
      text: "Well done! Will continue working with him in the near future.",
      author: "Sharon Sciammas",
      company: "Client Project, Netherlands",
      role: "Marketing Director",
      rating: 5,
      project: "Social Media Post Generator",
      image: "/api/placeholder/80/80",
      results: {
        metric: "100%",
        improvement: "Repeat client"
      }
    }
  ];

  useEffect(() => {
    if (!isAutoRotating) return;

    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [isAutoRotating, testimonials.length]);

  const nextTestimonial = () => {
    setIsAutoRotating(false);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setIsAutoRotating(false);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setIsAutoRotating(false);
    setCurrentIndex(index);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="testimonials section">
      <div className="container">
        <motion.div
          className="testimonials-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title gradient-text">Client Outcomes</h2>
          <p className="section-subtitle">
            Verified feedback from organizations that have deployed AI systems engineered to deliver measurable business impact
          </p>
        </motion.div>

        <div className="testimonials-container">
          <div className="testimonial-carousel">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial.id}
                className="testimonial-slide"
                initial={{ opacity: 0, x: 300 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -300 }}
                transition={{ duration: 0.5 }}
              >
                <div className="testimonial-content">
                  <div className="quote-icon">
                    {React.createElement(FaQuoteLeft as any)}
                  </div>
                  
                  <div className="testimonial-main">
                    <div className="testimonial-text">
                      <p>"{currentTestimonial.text}"</p>
                    </div>
                    
                    <div className="testimonial-author">
                      <div className="author-avatar">
                        <img 
                          src={currentTestimonial.image} 
                          alt={currentTestimonial.author}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                        <div className="avatar-placeholder">
                          {currentTestimonial.author.split(' ').map(name => name[0]).join('')}
                        </div>
                      </div>
                      
                      <div className="author-info">
                        <h4 className="author-name">{currentTestimonial.author}</h4>
                        <p className="author-role">{currentTestimonial.role}</p>
                        <p className="author-company">{currentTestimonial.company}</p>
                        
                        <div className="rating">
                          {[...Array(currentTestimonial.rating)].map((_, i) => (
                            React.createElement(FaStar as any, { key: i })
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="testimonial-results">
                    <div className="result-card">
                      <div className="result-metric gradient-text">
                        {currentTestimonial.results.metric}
                      </div>
                      <div className="result-label">
                        {currentTestimonial.results.improvement}
                      </div>
                    </div>
                    
                    <div className="project-tag">
                      <span>Project: {currentTestimonial.project}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="carousel-controls">
              <button 
                className="carousel-btn prev-btn" 
                onClick={prevTestimonial}
                aria-label="Previous testimonial"
              >
                {React.createElement(FaChevronLeft as any)}
              </button>
              
              <div className="carousel-indicators">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`indicator ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
              
              <button 
                className="carousel-btn next-btn" 
                onClick={nextTestimonial}
                aria-label="Next testimonial"
              >
                {React.createElement(FaChevronRight as any)}
              </button>
            </div>
          </div>

          <motion.div 
            className="auto-rotate-toggle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            <button 
              className={`toggle-btn ${isAutoRotating ? 'active' : ''}`}
              onClick={() => setIsAutoRotating(!isAutoRotating)}
            >
              {isAutoRotating ? 'Pause Auto-Rotation' : 'Resume Auto-Rotation'}
            </button>
          </motion.div>
        </div>

        <motion.div
          className="testimonials-stats"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="stat-item">
            <div className="stat-value gradient-text">100%</div>
            <div className="stat-label">Client Satisfaction</div>
          </div>
          <div className="stat-item">
            <div className="stat-value gradient-text">8+</div>
            <div className="stat-label">Real Testimonials</div>
          </div>
          <div className="stat-item">
            <div className="stat-value gradient-text">5⭐</div>
            <div className="stat-label">Average Rating</div>
          </div>
          <div className="stat-item">
            <div className="stat-value gradient-text">100%</div>
            <div className="stat-label">Repeat Clients</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Testimonials;